import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { Bell, XLg } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

import { notificationVerifyEmailModalVar } from "../../../graphql/globalVar/state";
import { useNotifications } from "../../../hooks/useNotifications";
import { useAuthService } from "../../../services/authService";
import { notificationService } from "../../../services/notificationService";
import Spinner from "../../ui/spinner";
import plug_img from "../img/messages.webp";

export default function NotificationsDropdown() {
  const { t } = useTranslation();
  const { needVerifyEmail } = useAuthService();
  const [show, setShow] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    loadMore,
    unreadCount,
    loading,
    hasMore,
    handleRemove,
    markAsRead,
  } = useNotifications();

  const handleClose = () => setShow(false);

  const handleMessageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.matches('[data-bs-target="#modalVerificationEmail"]')) {
      e.preventDefault();

      if (needVerifyEmail === false) {
        notificationService.info({ msg: t("emailVerify.alreadyVerified") });
        return;
      }

      notificationVerifyEmailModalVar(true);
    }
  };

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loading) loadMore();
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  return (
    <Dropdown show={show} onToggle={setShow} align="end">
      <Dropdown.Toggle
        id="notifications-dropdown"
        className="no-caret position-relative border-0 p-0 bg-background-gray rounded-5 d-flex align-items-center justify-content-center"
        style={{ width: "32px", height: "32px" }}
      >
        {unreadCount > 0 && (
          <div
            className="position-absolute bg-danger rounded-5 border border-white"
            style={{ width: "10px", height: "10px", top: "5px", right: "5px" }}
          />
        )}
        <Bell className="text-dark" size={21} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="notifications-dropdown__menu p-0 border-0"
        popperConfig={{
          modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
        }}
      >
        <div className="d-flex align-items-center justify-content-between p-3">
          <span className="fw-bold text-dark fs-5">
            {t("notifications.title", { count: unreadCount })}
          </span>
          <Button
            variant="link"
            className="notifications-dropdown__close text-dark p-0 border-0"
            aria-label={t("notifications.close")}
            onClick={handleClose}
          >
            <XLg size={16} />
          </Button>
        </div>

        <div className="notifications-dropdown__list d-grid gap-2 px-3 pb-3 pt-2 custom-scrollbar">
          {notifications.length
            ? notifications.map((item) => (
                <div
                  key={item.id}
                  className={classNames("p-3 rounded", {
                    "bg-success-bg": !item.wasRead,
                  })}
                  onClick={() => !item.wasRead && markAsRead(item.id)}
                >
                  <div
                    className="text-dark mb-2"
                    dangerouslySetInnerHTML={{ __html: item.message }}
                    onClick={handleMessageClick}
                  />
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <span className="text-info fs-7">
                      {item.createdAt} • Me-Ticket
                    </span>
                    <button
                      type="button"
                      className="notifications-dropdown__remove btn btn-link p-0 border-0 text-decoration-none text-success"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(item.id);
                      }}
                    >
                      {t("notifications.remove")}
                    </button>
                  </div>
                </div>
              ))
            : !loading && (
                <div className="d-flex flex-column align-items-center py-3">
                  <img
                    className="mb-3"
                    src={plug_img}
                    width={106}
                    height={72}
                    alt="messages"
                  />
                  <p className="text-info fs-7 m-0">
                    {t("notifications.empty")}
                  </p>
                </div>
              )}
          <div ref={sentinelRef}></div>
          {loading && <Spinner wrapperClassName="my-3 w-100" />}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
